import { useCallback, useState } from "react";

/** Synthetic event shape used by all config-driven onChange handlers */
export interface ChangeEventLike {
  target: {
    name: string;
    value: string;
    valueAsNumber?: number;
    validity?: { valid: boolean };
  };
}

/** Broader variant for components that type value as unknown (e.g. RangePicker) */
interface BroadChangeEvent {
  target: { name: string; value: unknown };
}

/** Union type accepted by setConfigState — covers native DOM events, ChangeEventLike, and BroadChangeEvent */
export type ConfigChangeEvent = ChangeEventLike | Event | BroadChangeEvent;

/** Initialize a config object by reading individual keys from localStorage */
export function restoreFromLocalStorage<T extends Record<string, unknown>>(defaultConfig: T): T {
  const result = { ...defaultConfig };
  for (const key of Object.keys(defaultConfig)) {
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      const defaultValue = defaultConfig[key];
      // Parse booleans correctly instead of leaving them as strings
      if (typeof defaultValue === "boolean") {
        (result as Record<string, unknown>)[key] = stored === "true";
      } else {
        (result as Record<string, unknown>)[key] = stored;
      }
    }
  }
  return result;
}

/**
 * React-state-backed config that mirrors every field to individual localStorage
 * keys.  Replaces the old deepSignal + useDeepSignalFromLocalStorage pattern.
 *
 * @returns [config, setConfigState, setConfigKeyValue]
 *   - config            – current config object (React state)
 *   - setConfigState    – event-handler setter (reads e.target.name / value)
 *   - setConfigKeyValue – programmatic setter for a single field
 */
export function useLocalStorageConfig<T extends Record<string, unknown>>(
  defaultConfig: T,
): [T, (e: ConfigChangeEvent) => void, (key: keyof T & string, value: unknown) => void] {
  const [config, setConfig] = useState<T>(() => restoreFromLocalStorage(defaultConfig));

  const setConfigState = useCallback((e: ConfigChangeEvent) => {
    const target = e.target as { name: string; value: string };
    const { name, value } = target;
    setConfig((prev) => ({ ...prev, [name]: value }));
    localStorage.setItem(name, value);
  }, []);

  const setConfigKeyValue = useCallback((key: keyof T & string, value: unknown) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    localStorage.setItem(key, String(value));
  }, []);

  return [config, setConfigState, setConfigKeyValue];
}
